// Google Analytics 4 for the websites (owner, 2026-09-15: "very robust analytics
// to show me who is visiting our website, which pages and how long").
//
// ── WHERE IT RUNS, AND WHERE IT MUST NOT ─────────────────────────────────────
// Only the public websites, only in a production build:
//   raisedbeef.com, www.raisedbeef.com, markets.raisedbeef.ai
// Never:
//   · the native apps (Capacitor) or a VITE_BARN_APP build — the App Store and
//     Play privacy answers declare no third-party analytics;
//   · cattleticker.raisedbeef.com or /lobby — a lobby TV left on all day would
//     make "time on site" meaningless;
//   · admin pages (/manage, /admin) — the owner's own clicks are not traffic;
//   · Vercel previews and localhost — not on the host list.
//
// ── PAGE VIEWS ARE SENT BY US, ON EACH ROUTE CHANGE ─────────────────────────
// `send_page_view: false`, and AnalyticsTracker sends one page_view per route.
// GA4's own "page changes based on browser history events" (Enhanced
// measurement) must be switched OFF in the data stream, or every in-app
// navigation counts twice — and it would also count the excluded pages.
//
// Google signals and ad personalisation are off: Google receives page views,
// not a name, email or account id. See legal/PRIVACY_POLICY.draft.md §5.

export const GA_MEASUREMENT_ID = "G-99MGJG26WP";

export const TRACKED_HOSTS = new Set(["raisedbeef.com", "www.raisedbeef.com", "markets.raisedbeef.ai", "cattlemarketcentral.com", "www.cattlemarketcentral.com"]);

const EXCLUDED_PATHS = [/^\/manage(\/|$)/, /^\/admin(\/|$)/, /^\/lobby(\/|$)/, /^\/board(\/|$)/];

/** Is this page view one we record? Pure, for tests. */
export function shouldTrack(hostname: string, pathname: string): boolean {
  if (!TRACKED_HOSTS.has(hostname)) return false;
  return !EXCLUDED_PATHS.some((re) => re.test(pathname));
}

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
    Capacitor?: { isNativePlatform?: () => boolean };
  }
}

let loaded = false;

/** True in a build and a browser where analytics may ever load. */
function allowedEnvironment(): boolean {
  if (typeof window === "undefined") return false;
  if (!import.meta.env.PROD) return false;
  if ((import.meta.env.VITE_BARN_APP as string | undefined)?.trim()) return false;
  if (window.Capacitor?.isNativePlatform?.()) return false;
  return TRACKED_HOSTS.has(window.location.hostname);
}

function load(): Gtag | null {
  if (loaded) return window.gtag ?? null;
  if (!allowedEnvironment()) return null;
  loaded = true;
  const layer: unknown[] = (window.dataLayer = window.dataLayer ?? []);
  const gtag: Gtag = function gtag() {
    // gtag.js reads the `arguments` object, not an array — keep it this shape.
    // eslint-disable-next-line prefer-rest-params
    layer.push(arguments);
  };
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
  return gtag;
}

/** Record one page view, if this page is one we record. Never throws. */
export function trackPageView(pathname: string, search: string): void {
  try {
    if (typeof window === "undefined" || !shouldTrack(window.location.hostname, pathname)) return;
    const gtag = load();
    if (!gtag) return;
    gtag("event", "page_view", {
      page_location: `${window.location.origin}${pathname}${search}`,
      page_path: pathname,
      page_title: document.title,
    });
  } catch {
    // Analytics must never break a page.
  }
}
